/**
 * Interaction timing tokens: the counterpart of the CSS tokens beside this
 * file, for behavior that runs in code rather than in a stylesheet.
 */

/**
 * How long a click waits for a second click, where a single click and a
 * double-click on the same target do different things (a group row's click
 * expands it, its double-click expands its whole level). Only
 * `useClickIntent` reads it; lint keeps click counting there.
 *
 * A double-click with no competing single click needs no wait: it uses the
 * browser's own `onDoubleClick`, which follows the user's system
 * double-click speed (an accessibility setting).
 */
export const CLICK_INTENT_WAIT_MS = 200

/** Header names also rename: allow the usual system double-click interval. */
export const HEADER_CLICK_INTENT_WAIT_MS = 500

/**
 * How long the pointer rests on something before its tooltip opens, so
 * passing over it on the way elsewhere opens nothing. Keyboard focus opens a
 * tooltip at once, and leaving closes a label at once (a card waits
 * `HOVER_MENU_CLOSE_DELAY_MS`). The shared `Tooltip` reads it.
 */
export const HOVER_INTENT_DELAY_MS = 300

/**
 * The navigation rail's quicker hover intent: how long the pointer rests on an
 * area's icon, or on the sidebar control in the header's corner, before
 * its menu or toggle opens. The rail itself
 * widens (Dynamic) the moment the pointer reaches it.
 */
export const RAIL_HOVER_INTENT_MS = 110

/**
 * While a menu is open beside the navigation rail, the pointer heading for it
 * (features/sidebar/logic/menuAim.ts) can cross other items without switching
 * it; an item it stops on takes over once it has rested this long. Scanning
 * (straight down the rail, back left) switches at once.
 */
export const RAIL_AIM_REST_MS = 80

/** Resting, for `RAIL_AIM_REST_MS`: the pointer stays within this many pixels. */
export const RAIL_REST_TOLERANCE_PX = 4

/**
 * How long a menu opened by hovering, or a tooltip's card (a description, an
 * InfoHint), stays open after the pointer leaves it, so crossing the gap
 * between its button and the menu or card doesn't close it.
 */
export const HOVER_MENU_CLOSE_DELAY_MS = 200

/** Extra room to recover a diagonal pointer path to Me's bottom-aligned menu. */
export const ME_MENU_CLOSE_GRACE_MS = 250

/**
 * How long a sidebar row takes to glide to its new place when the Recency
 * sort re-sorts the list (after the user stops using the sidebar), so a
 * moved table can be followed rather than jumping. Skipped when the user
 * prefers reduced motion.
 */
export const SIDEBAR_ROW_MOVE_MS = 200

/**
 * How long a Preference changed outside the Preferences page (the sidebar's
 * sort menu and folder button) waits for further changes before saving, so
 * quick clicks through the options send one save, not one per click. The
 * change shows at once either way.
 */
export const OWN_PREFERENCE_SAVE_DELAY_MS = 400
